/**
 * Meeting Types list page — thin wrapper around CRMObjectListPage.
 * The create flow is the shared create slideout driven by the booking-link
 * config (`bookingTypeCreateConfig`).
 */

import { CalendarCheck01, Copy01, Inbox01, Trash01 } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useUser } from '@/allauth_auth/hooks';
import { useOrg } from '@/contexts/OrgContext';
import { useCurrentUserEntity } from '@/hooks/useCurrentUserEntity';
import { useOrgMembers } from '@/hooks/useOrgMembers';
import { useCanWrite } from '@/hooks/usePermissions';
import { updateBookingType } from '@/lib/api/scheduling';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import type {
  BuiltInViewConfig,
  ListPageConfig,
  RowAction,
} from '@/lib/crm/core/types';
import { buildBookingTypeCreateConfig } from '@/lib/crm/create/configs/bookingTypeCreateConfig';
import { useCreateSlideout } from '@/lib/crm/create/CreateSlideoutContext';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import { bookingTypeListConfig, CRMObjectListPage } from '@/lib/crm/list';
import { submitEventsOptimistic } from '@/lib/crm/store/submitEventsOptimistic';
import { buildPreferredBookingUrl } from '@/lib/scheduling/buildBookingUrl';
import { useBookingLinkBaseUrl } from '@/lib/scheduling/useBookingLinkBaseUrl';
import { useOrgSchedulingDefaults } from '@/lib/scheduling/useOrgSchedulingDefaults';
import { useViewHealthMarker } from '@/lib/view-health';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

export default function MeetingTypesPage() {
  const user = useUser();
  const { currentOrg } = useOrg();
  const { members: orgMembers } = useOrgMembers(currentOrg?.id ?? null);
  const { orgId } = useParams<{ orgId: string }>();
  const { entityId: currentUserEntityId } = useCurrentUserEntity();
  // Configured booking-link domain (e.g. https://meet.ahoy.ai). Falls back
  // to window.location.origin while loading or when no override is set.
  const { baseUrl: bookingLinkBaseUrl } = useBookingLinkBaseUrl(
    currentOrg?.id ?? null
  );
  // Org-wide scheduling defaults seed the create form. Fetched here rather than
  // in the form so the org entity has resolved by the time the user clicks
  // "New booking link" — the config's initialValues are read once, on open.
  const {
    defaults: orgSchedulingDefaults,
    isLoading: orgDefaultsLoading,
    error: orgDefaultsError,
  } = useOrgSchedulingDefaults(currentOrg?.id, currentOrg?.entityId);
  const { open: openCreateSlideout } = useCreateSlideout();
  const { openSlideout } = useSlideoutStack();
  const canWrite = useCanWrite();

  useViewHealthMarker('meeting-types-list-ready', { ready: true });

  // ── Row actions ─────────────────────────────────────────────────────────────

  const handleCopyLink = useCallback(
    (obj: CrmObject) => {
      const globalSlug = getPropertyValue<string>(
        obj.properties?.ahoy_global_slug
      );
      const baseUrl = bookingLinkBaseUrl ?? window.location.origin;
      const url = buildPreferredBookingUrl({ baseUrl, globalSlug });
      if (!url) {
        toast.error('Cannot build booking link');
        return;
      }
      if (!navigator.clipboard?.writeText) {
        toast.error('Clipboard not available');
        return;
      }
      navigator.clipboard.writeText(url).then(
        () => toast.success('Booking link copied'),
        () => toast.error("We couldn't copy the booking link. Try again.")
      );
    },
    [bookingLinkBaseUrl]
  );

  const handleArchive = useCallback(
    async (obj: CrmObject) => {
      if (!currentOrg) {
        console.warn('handleArchive: currentOrg is null');
        toast.error('Cannot archive: no active organization');
        return;
      }
      try {
        await updateBookingType(currentOrg.id, obj.entity_id, {
          is_active: false,
        });
        toast.success('Booking link archived');
      } catch (err) {
        console.error('Archive booking link failed', {
          entityId: obj.entity_id,
          orgId: currentOrg.id,
          error: err,
        });
        toast.error("We couldn't archive this booking link. Try again.");
      }
    },
    [currentOrg]
  );

  const handleDelete = useCallback(
    async (obj: CrmObject) => {
      if (!currentOrg || !user?.id) {
        console.warn('handleDelete: currentOrg or user is null');
        toast.error('Cannot delete: no active organization or user');
        return;
      }
      const client = createCRMObjectClient(currentOrg.id, 'ahoy_booking_type');
      try {
        const freshObject = await client.getByEntityId(obj.entity_id);
        await submitEventsOptimistic({
          orgId: currentOrg.id,
          userId: user.id,
          entityTypeName: 'ahoy_booking_type',
          primaryEntityId: freshObject.entity_id,
          fallbackSeq: freshObject.last_event_seq,
          errorToast: false,
          build: b =>
            b.objectDeleted(freshObject.entity_id, 'ahoy_booking_type'),
        });
        toast.success('Booking link deleted');
      } catch (err) {
        console.error('Delete booking link failed', {
          entityId: obj.entity_id,
          orgId: currentOrg.id,
          error: err,
        });
        toast.error("We couldn't delete this booking link. Try again.");
      }
    },
    [currentOrg, user]
  );

  // ── Config with row actions ──────────────────────────────────────────────────

  const rowActions = useMemo<RowAction[]>(
    () => [
      {
        id: 'copy-link',
        label: 'Copy link',
        icon: Copy01,
        onClick: handleCopyLink,
      },
      ...(canWrite
        ? [
            {
              id: 'archive',
              label: 'Archive',
              icon: Inbox01,
              onClick: handleArchive,
              isDisabled: (obj: CrmObject) =>
                getPropertyValue<string>(obj.properties?.ahoy_is_active) ===
                'false',
            } satisfies RowAction,
          ]
        : []),
      ...(canWrite
        ? [
            {
              id: 'delete',
              label: 'Delete',
              icon: Trash01,
              variant: 'danger' as const,
              onClick: handleDelete,
            } satisfies RowAction,
          ]
        : []),
    ],
    [canWrite, handleCopyLink, handleArchive, handleDelete]
  );

  const builtInViews = useMemo<BuiltInViewConfig[]>(() => {
    const activeCondition = {
      id: 'builtin-is-active',
      field: 'ahoy_is_active',
      operator: 'equals' as const,
      value: 'true',
    };
    return [
      {
        id: 'my-booking-links',
        label: 'My booking links',
        // Drop the owner clause until the current user's entity id resolves so
        // we never query an empty owner value (shows the all-owners active
        // baseline in the meantime).
        baselineConditions: currentUserEntityId
          ? [
              activeCondition,
              {
                id: 'builtin-owner-me',
                field: 'ahoy_booking_type_to_owner',
                operator: 'equals' as const,
                value: currentUserEntityId,
                association: 'ahoy_booking_type_to_owner',
              },
            ]
          : [activeCondition],
      },
      {
        id: 'all-booking-links',
        label: 'All booking links',
        baselineConditions: [activeCondition],
      },
    ];
  }, [currentUserEntityId]);

  const configWithActions = useMemo<ListPageConfig>(
    () => ({ ...bookingTypeListConfig, builtInViews, rowActions }),
    [builtInViews, rowActions]
  );

  // ── Create slideout config ───────────────────────────────────────────────────

  const createMutationFn = useCallback(
    async ({
      properties,
      teamMemberUserIds,
    }: {
      properties: Record<string, unknown>;
      teamMemberUserIds?: string[];
    }) => {
      if (!currentOrg || !user?.id) {
        throw new Error('Missing org or user');
      }
      const entityId = crypto.randomUUID();
      await submitEventsOptimistic({
        orgId: currentOrg.id,
        userId: user.id,
        entityTypeName: 'ahoy_booking_type',
        primaryEntityId: entityId,
        errorToast: false,
        build: b => {
          b.objectCreated(entityId, 'ahoy_booking_type', properties);
        },
      });

      // Team members are deliberately NOT part of the batch above.
      // `ahoy_booking_type_to_team_members` is `value_write_access: internal`, so the
      // events API rejects it from a user-sourced submit — and because a batch is
      // atomic, including it failed the whole create, making Collective and Round
      // Robin links impossible to create at all. The scheduling PATCH endpoint is the
      // sanctioned path: it writes the association with the internal API after
      // checking every id is an active, non-deactivated member of this org.
      const teamMembers = teamMemberUserIds ?? [];
      if (teamMembers.length > 0) {
        try {
          await updateBookingType(currentOrg.id, entityId, {
            team_member_user_ids: teamMembers,
          });
        } catch (err) {
          // The booking link itself exists at this point, so say so rather than
          // letting a bare failure imply nothing was created.
          console.error('Failed to save booking link team members', {
            entityId,
            orgId: currentOrg.id,
            error: err,
          });
          throw new Error(
            "Booking link created, but its team members couldn't be saved. Open the link to add them."
          );
        }
      }
      return { entity_id: entityId };
    },
    [currentOrg, user]
  );

  // Open the new booking link's record once it exists.
  const handleCreated = useCallback(
    (entityId: string) => openSlideout(entityId),
    [openSlideout]
  );

  const handleCreateClick = useCallback(() => {
    if (!currentOrg) {
      return;
    }
    openCreateSlideout(
      buildBookingTypeCreateConfig(
        currentOrg.id,
        orgSchedulingDefaults,
        orgMembers,
        createMutationFn,
        handleCreated
      )
    );
  }, [
    currentOrg,
    orgSchedulingDefaults,
    orgMembers,
    createMutationFn,
    handleCreated,
    openCreateSlideout,
  ]);

  const bookingsHeaderAction = useMemo(
    () => [
      {
        id: 'view-bookings',
        label: 'View Bookings',
        href: `/${orgId}/crm/bookings`,
        icon: CalendarCheck01,
        variant: 'unstyled' as const,
      },
    ],
    [orgId]
  );

  return (
    <CRMObjectListPage
      config={configWithActions}
      // Withhold the create action until the org's scheduling defaults have
      // resolved (or failed): otherwise a user who clicks "New booking link"
      // before the ahoy_org entity fetch completes gets a form seeded from
      // {} instead of the org's real defaults. Gating on error too (not just
      // isLoading) matters — isLoading goes false on its own after a failed
      // fetch, and re-enabling with empty defaults would silently reproduce
      // the bug this exists to prevent.
      onCreateClick={
        canWrite && !orgDefaultsLoading && !orgDefaultsError
          ? handleCreateClick
          : undefined
      }
      extraHeaderActions={bookingsHeaderAction}
    />
  );
}
